import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import type { StyleListProps } from './types';
import StylistCarousel from './StylistCarousel';
import StylistGrid from './StylistGrid';
import StylistGridMobile from './StylistGridMobile';

const StyleList: React.FC<StyleListProps> = ({ data, className = '' }) => {
  // Prioritize props data over Redux data
  const stylists = useMemo(() => {
    // If data is passed as prop and has stylists, use it
    if (data?.stylists && data.stylists.length > 0) {
      return data.stylists;
    }
    // Otherwise return empty array
    return [];
  }, [data?.stylists]);
  
  // Check for reduced motion preference - MUST be before early return
  const prefersReducedMotion = useMemo(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  // Determine if we should use carousel (only if more than 4 stylists)
  const shouldUseCarousel = stylists.length > 4;
  
  // Don't render if no stylists - MUST be after all hooks
  if (!stylists || stylists.length === 0) {
    return null;
  }

  return (
    <motion.section
      id="stylists"
      className={`py-16 px-6 md:px-12 bg-gray-50 dark:bg-custom-dark transition-colors duration-300 ${className}`}
      initial={prefersReducedMotion ? {} : { opacity: 0 }}
      whileInView={prefersReducedMotion ? {} : { opacity: 1 }}
      transition={prefersReducedMotion ? {} : { duration: 0.6 }}
      viewport={{ once: true, margin: '-100px' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-8 sm:mb-12 md:mb-16"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          transition={prefersReducedMotion ? {} : { duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
            Our Stylists
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300">
            Meet our talented team of beauty experts
          </p>
        </motion.div>

        {/* Mobile View - Tailwind Responsive */}
        <div className="block sm:hidden">
          <StylistGridMobile stylists={stylists} />
        </div>

        {/* Desktop View - Tailwind Responsive */}
        <div className="hidden sm:block">
          {shouldUseCarousel ? (
            <StylistCarousel 
              stylists={stylists} 
              prefersReducedMotion={prefersReducedMotion} 
            />
          ) : (
            <StylistGrid stylists={stylists} />
          )}
        </div>
      </div>
    </motion.section>
  );
};

export default StyleList;
